import React, {
  useRef, useEffect, useState, useMemo,
} from 'react';
import useNebula from 'hooks/useNebula';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import Container from '@material-ui/core/Container';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import * as d3 from 'd3';
import useQlikSessionObject from 'hooks/useQlikSessionObject';
import useQlikApp from 'hooks/useQlikApp';
import SliderUI from '../Slider';
import Filter from '../Filter';
import CheckboxFilter from '../CheckboxFilter';
import AllTime from '../AllTime';
import Results from '../Results';
import data from '../../data';

const useStyles = makeStyles((theme) => ({
  vizWrapper: {
    marginTop: 40,
  },
  vizTitle: {
    paddingBottom: 10,
    borderBottom: '1px solid #404040',
    [theme.breakpoints.down('sm')]: {
      '& h2': {
        fontSize: '1.85rem',
      },
    },
    [theme.breakpoints.down('xs')]: {
      '& h2': {
        fontSize: '1.4rem',
      },
    },
  },
  viz: {
    height: '450px',
    '& > div': {
      backgroundColor: '#FFFDED',
    },
    [theme.breakpoints.down('xs')]: {
      height: '250px',
    },
  },
  vizFilters: {
    position: 'relative',
  },
  filters: {
    display: 'flex',
    alignItems: 'center',
    position: 'absolute',
    right: 0,
    '@media (max-width: 767px)': {
      position: 'relative',
    },
    [theme.breakpoints.down('xs')]: {
      position: 'relative',
      justifyContent: 'space-evenly',
    },
  },
  filter: {
    marginLeft: 20,
  },
  results: {
    backgroundColor: '#F6F9E6',
    backgroundImage: 'url(./assets/medal.png)',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'right top',
    padding: '30px 0 30px 0',
    marginTop: '20px',
  },
  allTime: {
    padding: '30px 0 60px 0',
  },
}));

const Viz = ({ width, lang }) => {
  const qlikApp = useQlikApp();
  const nebula = useNebula();
  const elementRef = useRef();
  const vizRef = useRef();

  const [selectedCountryNameState, setSelectedCountryNameState] = useState('');
  const [countryMedalsState, setCountryMedalsState] = useState([]);

  useEffect(() => {
    if (!nebula) return;
    (async () => {
      vizRef.current = await nebula.render({
        element: elementRef.current,
        type: 'dorling-cartogram',
        fields: [
          {
            qDef: { qFieldDefs: ['[Team]'], qFieldLabels: ['Team'], qSortCriterias: [{ qSortByAscii: 1 }] },
            qAttributeExpressions: [
              {
                qExpression: '=Only({<[Team]=>} [ISO])',
              },
              {
                qExpression: '=Only({<[Team]=>} [Continent])',
              },
              {
                qExpression: '=Only({<[Team]=>} [YearCity])',
              },
            ],
            qNullSuppression: true,
          },
          {
            qDef: { qDef: '=Sum({<[Team]=>} [Total])', qLabel: 'Medals' },
            qAttributeExpressions: [
              {
                qExpression: '=Sum({<[Team]=>} [Gold])',
              },
              {
                qExpression: '=Sum({<[Team]=>} [Silver])',
              },
              {
                qExpression: '=Sum({<[Team]=>} [Bronze])',
              },
            ],
          },
        ],
      });
    })();
  }, [nebula]);

  const [medalsState, setMedalsState] = useState({
    Gold: false,
    Silver: false,
    Bronze: false,
  });

  const handleMedalsStateChange = (target, checked) => {
    setMedalsState({ ...medalsState, [target]: checked });
  };

  const handleCountryStateChange = (country) => {
    setSelectedCountryNameState(country);
  };

  const qMedalCountData = useQlikSessionObject({
    app: qlikApp,
    properties: {
      qHyperCubeDef: {
        qDimensions: [
          {
            qDef: { qFieldDefs: ['[YearCity]'], qFieldLabels: ['YearCity'] },
          },
          {
            qDef: { qFieldDefs: ['[Team]'], qFieldLabels: ['Team'] },
          },
        ],
        qMeasures: [
          { qDef: { qDef: `Sum({<[Team]={"${selectedCountryNameState}"}, [YearCity]=>} [Total])`, qLabel: 'Medals' } },
        ],
      },
    },
  });

  const medalsCountdata = useMemo(() => {
    if (!qMedalCountData.layout) return [];
    return qMedalCountData.layout.qHyperCube.qDataPages[0].qMatrix.map((row) => ({
      year: row[0].qElemNumber,
      country: row[1].qText,
      medals: row[2].qText,
      medalsScaled: row[2].qText,
    }));
  }, [qMedalCountData.layout]);

  useEffect(() => {
    const max = d3.max(medalsCountdata, (d) => +d.medals);
    const scale = d3.scaleLinear()
      .domain([0, max])
      .range([5, 20]);
    const scaledData = medalsCountdata.map((element) => ({ ...element, medalsScaled: scale(element.medalsScaled) }));
    setCountryMedalsState(scaledData);
  }, [medalsCountdata]);

  const classes = useStyles();
  return (
    <>
      <Container maxWidth="lg">
        <Box className={classes.vizWrapper}>
          <Box className={classes.vizTitle}>
            <Typography variant="h2">{lang === 'jp' ? data.jp.viz.title : data.en.viz.title}</Typography>
          </Box>
          <div id="cartogramViz" className={classes.viz} ref={elementRef} />
          <Box className={classes.vizFilters}>
            <Box className={classes.filters}>
              { isWidthUp('sm', width) && <CheckboxFilter ref={vizRef} medalsState={medalsState} handleMedalsStateChange={handleMedalsStateChange} lang={lang} />}
              { isWidthUp('sm', width) && <Filter field="Sport" label={data.en.filters.Sport} lang={lang} /> }
              { !isWidthUp('sm', width) && <Filter field="YearCity" label={data.en.filters.Year} lang={lang} /> }
              <Filter field="Team" label={data.en.filters.Geography} lang={lang} handleCountryStateChange={handleCountryStateChange} />
            </Box>
            { isWidthUp('sm', width) && <SliderUI countryMedalsState={countryMedalsState} lang={lang} /> }
          </Box>
        </Box>
      </Container>
      <Box className={classes.results}>
        <Container maxWidth="lg">
          <Results vizRef={vizRef} medalsState={medalsState} handleMedalsStateChange={handleMedalsStateChange} lang={lang} />
        </Container>
      </Box>
      <Box className={classes.allTime}>
        <Container maxWidth="lg">
          <AllTime vizRef={vizRef} medalsState={medalsState} handleMedalsStateChange={handleMedalsStateChange} lang={lang} />
        </Container>
      </Box>
    </>
  );
};

Viz.propTypes = {
  width: PropTypes.string,
  lang: PropTypes.string,
};

Viz.defaultProps = {
  width: 'sm',
  lang: 'en',
};

export default withWidth()(Viz);
